import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, ExternalLink, ShieldCheck, Euro } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const settings = useSettings();
  const [form, setForm] = useState({
    name: "", specialty: "", license_number: "", rempe_code: "", college: "", region: "", nif: "",
  });
  const [stripeKey, setStripeKey] = useState("");
  const [stripeStatus, setStripeStatus] = useState({ has_own_key: false, platform_fallback: true });
  const [price, setPrice] = useState(20);
  const [savingPrice, setSavingPrice] = useState(false);

  useEffect(() => { setPrice(settings.consultation_price_eur || 20); }, [settings.consultation_price_eur]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        specialty: user.specialty || "",
        license_number: user.license_number || "",
        rempe_code: user.rempe_code || "",
        college: user.college || "",
        region: user.region || "",
        nif: user.nif || "",
      });
    }
    api.get("/doctor/stripe-key/status").then(({ data }) => setStripeStatus(data)).catch(() => {});
  }, [user]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/doctor/profile", form);
      await refresh();
      toast.success(t("saved"));
    } catch (err) { toast.error(formatError(err)); }
  };

  const saveStripeKey = async () => {
    try {
      const { data } = await api.put("/doctor/stripe-key", { stripe_api_key: stripeKey });
      setStripeStatus((p) => ({ ...p, has_own_key: data.configured }));
      setStripeKey("");
      toast.success("Clave Stripe guardada");
    } catch (err) { toast.error(formatError(err)); }
  };

  const removeStripeKey = async () => {
    try {
      await api.put("/doctor/stripe-key", { stripe_api_key: "" });
      setStripeStatus((p) => ({ ...p, has_own_key: false }));
      toast.success("Clave Stripe eliminada");
    } catch (err) { toast.error(formatError(err)); }
  };

  const savePrice = async () => {
    const v = parseFloat(price);
    if (isNaN(v) || v <= 0) { toast.error("Precio inválido"); return; }
    setSavingPrice(true);
    try {
      await api.put("/settings", { consultation_price_eur: v });
      settings.refresh && settings.refresh();
      toast.success(`Precio actualizado a ${v}€`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setSavingPrice(false); }
  };

  return (
    <div className="space-y-6 fade-up max-w-2xl" data-testid="profile-page">
      <h1 className="font-display text-3xl">{t("profile")}</h1>

      <form onSubmit={saveProfile} className="space-y-4">
        <Card>
          <CardHeader><CardTitle className="font-display">{t("profile")} REMPE</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>{t("name")}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" /></div>
            <div><Label>Especialidad</Label><Input value={form.specialty} onChange={(e) => set("specialty", e.target.value)} data-testid="profile-specialty" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("license_number")}</Label><Input value={form.license_number} onChange={(e) => set("license_number", e.target.value)} data-testid="profile-license" /></div>
              <div><Label>{t("rempe_code")}</Label><Input value={form.rempe_code} onChange={(e) => set("rempe_code", e.target.value)} placeholder="Pendiente del Colegio" data-testid="profile-rempe" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("college")}</Label><Input value={form.college} onChange={(e) => set("college", e.target.value)} data-testid="profile-college" /></div>
              <div><Label>{t("region")}</Label><Input value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="profile-region" /></div>
            </div>
            <div><Label>{t("nif")}</Label><Input value={form.nif} onChange={(e) => set("nif", e.target.value)} data-testid="profile-nif" /></div>
          </CardContent>
        </Card>
        <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-profile-btn">{t("save_profile")}</Button>
      </form>

      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Euro className="w-5 h-5" /> Precio de la consulta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Este precio se aplica automáticamente a todas las nuevas reservas (Stripe + Wise + WhatsApp).
            Las citas ya creadas conservan su precio original.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label>Precio actual (EUR)</Label>
              <Input
                type="number"
                min="1"
                step="0.5"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="price-input"
              />
            </div>
            <Button
              onClick={savePrice}
              disabled={savingPrice || parseFloat(price) === settings.consultation_price_eur}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-price-btn"
            >
              {savingPrice ? "..." : `Guardar ${price}€`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <CreditCard className="w-5 h-5" /> Stripe — Cobros directos a tu banco
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-[#F3F1EC] rounded-lg p-4 text-sm space-y-2">
            <div className="flex items-center gap-2 font-medium">
              <ShieldCheck className="w-4 h-4 text-primary" />
              {stripeStatus.has_own_key
                ? "Tu cuenta Stripe está conectada — los pagos van directamente a tu banco."
                : "Estás usando la clave Stripe de demostración (modo test). Para que los pagos lleguen a tu banco, conecta tu cuenta."}
            </div>
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Crea una cuenta en <a href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer" className="text-primary underline">stripe.com</a> y verifica tu identidad.</li>
              <li>En el dashboard de Stripe ve a <strong>Settings → Payouts</strong> y añade tu cuenta bancaria (IBAN ES...).</li>
              <li>En <strong>Developers → API keys</strong> copia tu <strong>Secret key</strong> (sk_live_... para producción, sk_test_... para pruebas).</li>
              <li>Pégala abajo y guarda. Cada pago de 20€ irá directo a tu banco (Stripe descuenta su comisión ~1.5% + 0.25€).</li>
            </ol>
          </div>

          {stripeStatus.has_own_key ? (
            <div className="flex items-center justify-between border border-border rounded-lg p-3" data-testid="stripe-connected">
              <div>
                <div className="font-medium text-[#4A6E4F]">✓ Cuenta Stripe conectada</div>
                <div className="text-xs text-muted-foreground">Los pagos llegarán a tu banco configurado en Stripe.</div>
              </div>
              <Button variant="ghost" size="sm" onClick={removeStripeKey} data-testid="stripe-remove-btn">Desconectar</Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Tu Stripe Secret Key</Label>
              <Input
                type="password"
                placeholder="sk_live_... o sk_test_..."
                value={stripeKey}
                onChange={(e) => setStripeKey(e.target.value)}
                data-testid="stripe-key-input"
              />
              <Button onClick={saveStripeKey} disabled={!stripeKey} className="bg-primary hover:bg-primary/90" data-testid="stripe-save-btn">
                Conectar mi Stripe
              </Button>
            </div>
          )}
          <a href="https://dashboard.stripe.com/account/payouts" target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Configurar mi banco en Stripe
          </a>
        </CardContent>
      </Card>

      <PrivacySection />
    </div>
  );
}

function PrivacySection() {
  const [twoFA, setTwoFA] = useState({ open: false, secret: "", uri: "", code: "" });
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setEnabled(!!data?.two_fa_enabled)).catch(() => {});
  }, []);

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/data-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `mis-datos-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tus datos se han descargado");
    } catch (e) { toast.error(formatError(e)); }
  };

  const deleteAccount = async () => {
    const confirm = window.prompt('Escribe ELIMINAR para confirmar el borrado de tu cuenta. Tu historia clínica se conservará anonimizada según la Ley 41/2002.');
    if (confirm !== "ELIMINAR") return;
    try {
      await api.delete("/me/account");
      toast.success("Cuenta eliminada. Te redirigimos...");
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (e) { toast.error(formatError(e)); }
  };

  const enable2FA = async () => {
    try {
      const { data } = await api.post("/me/2fa/enable");
      setTwoFA({ open: true, secret: data.secret, uri: data.otpauth_uri, code: "" });
    } catch (e) { toast.error(formatError(e)); }
  };

  const verify2FA = async () => {
    try {
      await api.post("/me/2fa/verify", { code: twoFA.code });
      toast.success("2FA activado");
      setTwoFA({ open: false, secret: "", uri: "", code: "" });
      setEnabled(true);
    } catch (e) { toast.error(formatError(e)); }
  };

  const disable2FA = async () => {
    if (!window.confirm("¿Desactivar 2FA?")) return;
    try {
      await api.post("/me/2fa/disable");
      setEnabled(false);
      toast.success("2FA desactivado");
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Card data-testid="privacy-section">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" /> Mis datos y privacidad
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="bg-[#F8F7F2] border rounded-lg p-4">
          <div className="font-medium text-sm mb-1">Autenticación en dos pasos (2FA)</div>
          <div className="text-xs text-muted-foreground mb-3">Añade una capa extra de seguridad usando una app autenticadora (Google Authenticator, Authy...).</div>
          {enabled ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-emerald-700 font-medium">✓ 2FA activado</span>
              <Button size="sm" variant="outline" onClick={disable2FA} data-testid="disable-2fa">Desactivar</Button>
            </div>
          ) : !twoFA.open ? (
            <Button size="sm" onClick={enable2FA} className="bg-primary hover:bg-primary/90" data-testid="enable-2fa">Activar 2FA</Button>
          ) : (
            <div className="space-y-3 mt-2">
              <div className="text-xs">Escanea este código QR en tu app autenticadora o copia el secreto manualmente:</div>
              <div className="font-mono text-xs bg-white border rounded p-2 break-all">{twoFA.secret}</div>
              <a className="text-xs text-primary underline" href={twoFA.uri}>Abrir en app autenticadora</a>
              <Input placeholder="Código de 6 dígitos" value={twoFA.code} onChange={(e) => setTwoFA({ ...twoFA, code: e.target.value })} maxLength={6} data-testid="2fa-code-input" />
              <Button size="sm" onClick={verify2FA} className="bg-primary hover:bg-primary/90" data-testid="verify-2fa">Verificar y activar</Button>
            </div>
          )}
        </div>

        <div className="bg-[#F8F7F2] border rounded-lg p-4">
          <div className="font-medium text-sm mb-1">Derecho de acceso y portabilidad (RGPD Art. 15 y 20)</div>
          <div className="text-xs text-muted-foreground mb-3">Descarga una copia completa de todos tus datos personales en formato JSON estructurado.</div>
          <Button size="sm" variant="outline" onClick={exportData} data-testid="export-data-btn">Descargar mis datos</Button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="font-medium text-sm mb-1 text-red-900">Derecho de supresión (RGPD Art. 17)</div>
          <div className="text-xs text-red-800 mb-3">
            Elimina tu cuenta. <strong>Importante:</strong> tu historia clínica, recetas y exámenes se conservarán
            anonimizados durante 15 años conforme a la Ley 41/2002 (autonomía del paciente), pero ya no estarán
            asociados a tus datos identificativos.
          </div>
          <Button size="sm" variant="destructive" onClick={deleteAccount} data-testid="delete-account-btn">Eliminar mi cuenta</Button>
        </div>

        <div className="text-xs text-muted-foreground border-t pt-3">
          Para más información sobre tus derechos consulta nuestra{" "}
          <a href="/privacidad" target="_blank" className="text-primary underline">Política de Privacidad</a>.
          Reclamaciones ante la <a href="https://aepd.es" target="_blank" rel="noreferrer" className="text-primary underline">AEPD</a>.
        </div>
      </CardContent>
    </Card>
  );
}
